/**
 * @fileoverview useEndlessRunner Hook
 * @description Core game logic hook for Endless Runner
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { logActivity } from "@/lib/activity-log";
import { notifyTeamOfPoints } from "@/modules/games/utils/teamNotifications";
import {
  RunnerDifficulty,
  RunnerGameState,
  RunnerResult,
  RunnerUserStats,
  RunnerWeekendConfig,
  EMPTY_RUNNER_STATS,
  DEFAULT_RUNNER_WEEKEND_CONFIG,
} from "../types";

type SessionMode = "free" | "weekend";

interface UseEndlessRunnerReturn {
  /** Current game state */
  gameState: RunnerGameState;
  /** Current session ID */
  sessionId: string | null;
  /** Game result (after game over) */
  result: RunnerResult | null;
  /** Is loading */
  isLoading: boolean;
  /** Start a new game */
  startGame: (difficulty: RunnerDifficulty) => Promise<void>;
  /** Update score during gameplay */
  updateScore: (score: number) => void;
  /** Record obstacle avoided */
  recordObstacleAvoided: () => void;
  /** Record a jump */
  recordJump: () => void;
  /** End the game */
  endGame: (finalScore: number, obstaclesAvoided: number) => Promise<void>;
  /** Pause the game */
  pauseGame: () => void;
  /** Resume the game */
  resumeGame: () => void;
  /** Reset for new game */
  resetGame: () => void;
}

interface UseEndlessRunnerOptions {
  /** Session mode */
  mode: SessionMode;
  /** Weekend config (for weekend mode) */
  weekendConfig?: RunnerWeekendConfig;
}

/**
 * Hook for managing Endless Runner game logic
 */
export const useEndlessRunner = (
  options: UseEndlessRunnerOptions
): UseEndlessRunnerReturn => {
  const { mode, weekendConfig = DEFAULT_RUNNER_WEEKEND_CONFIG } = options;
  const { activeProfile, activeWeekend, userTeam } = useAuth();

  const [gameState, setGameState] = useState<RunnerGameState>({
    score: 0,
    highScore: 0,
    speed: 4,
    isPlaying: false,
    isPaused: false,
    isGameOver: false,
    jumps: 0,
    obstaclesAvoided: 0,
    difficulty: "medium",
    timePlayedSeconds: 0,
    startedAt: null,
    endedAt: null,
  });

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [result, setResult] = useState<RunnerResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Timer ref
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);

  /**
   * Start timer
   */
  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setGameState((prev) => ({
        ...prev,
        timePlayedSeconds: elapsed,
      }));
    }, 1000);
  }, []);

  /**
   * Stop timer
   */
  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /**
   * Calculate points earned
   */
  const calculatePoints = useCallback(
    (score: number): number => {
      if (mode !== "weekend") return 0;

      let points = Math.floor(score / 1000) * weekendConfig.points_per_1000_score;

      // Add milestone bonuses
      if (score >= 25000) {
        points += weekendConfig.milestone_bonuses.score_25000;
      } else if (score >= 10000) {
        points += weekendConfig.milestone_bonuses.score_10000;
      } else if (score >= 5000) {
        points += weekendConfig.milestone_bonuses.score_5000;
      }

      return points;
    },
    [mode, weekendConfig]
  );

  /**
   * Start a new game
   */
  const startGame = useCallback(
    async (difficulty: RunnerDifficulty) => {
      if (!activeProfile?.id) return;

      setIsLoading(true);
      setResult(null);

      try {
        // Get user's current high score for this difficulty
        const { data: statsData } = await supabase
          .from("user_game_stats")
          .select("stats")
          .eq("user_id", activeProfile.id)
          .eq("game_slug", "endless_runner")
          .eq("mode", mode)
          .single();

        const stats = (statsData?.stats as RunnerUserStats) || EMPTY_RUNNER_STATS;
        const highScore = stats[difficulty]?.best_score || 0;

        // Create initial game state
        const initialState = {
          score: 0,
          highScore,
          difficulty,
          isPlaying: true,
          isPaused: false,
          isGameOver: false,
          jumps: 0,
          obstaclesAvoided: 0,
          startedAt: new Date().toISOString(),
          endedAt: null,
          timePlayedSeconds: 0,
        };

        // Create game session
        const { data: session, error: sessionError } = await supabase
          .from("game_sessions")
          .insert({
            game_slug: "endless_runner",
            created_by: activeProfile.id,
            participants: [
              {
                user_id: activeProfile.id,
                role: "player",
                name: activeProfile.full_name ?? "Speler",
                team_id: userTeam?.id,
              },
            ],
            mode,
            session_type: "solo",
            status: "active",
            game_state: initialState,
            weekend_id: mode === "weekend" ? activeWeekend?.id : null,
          })
          .select()
          .single();

        if (sessionError || !session) {
          throw new Error("Could not create game session");
        }

        setSessionId(session.id);
        setGameState({
          ...initialState,
          speed: 4,
        });

        startTimer();
      } catch (err) {
        console.error("[useEndlessRunner] Error starting game:", err);
      } finally {
        setIsLoading(false);
      }
    },
    [activeProfile, userTeam, activeWeekend, mode, startTimer]
  );

  /**
   * Update score
   */
  const updateScore = useCallback((score: number) => {
    setGameState((prev) => ({
      ...prev,
      score,
    }));
  }, []);

  /**
   * Record obstacle avoided
   */
  const recordObstacleAvoided = useCallback(() => {
    setGameState((prev) => ({
      ...prev,
      obstaclesAvoided: prev.obstaclesAvoided + 1,
    }));
  }, []);

  /**
   * Record jump
   */
  const recordJump = useCallback(() => {
    setGameState((prev) => ({
      ...prev,
      jumps: prev.jumps + 1,
    }));
  }, []);

  /**
   * End the game
   */
  const endGame = useCallback(
    async (finalScore: number, obstaclesAvoided: number) => {
      if (!activeProfile?.id || !sessionId) return;

      stopTimer();

      const endedAt = new Date().toISOString();
      const timePlayedSeconds = Math.floor(
        (Date.now() - startTimeRef.current) / 1000
      );
      const pointsEarned = calculatePoints(finalScore);
      const isNewHighScore = finalScore > gameState.highScore;

      setGameState((prev) => ({
        ...prev,
        isPlaying: false,
        isGameOver: true,
        endedAt,
        score: finalScore,
        obstaclesAvoided,
        timePlayedSeconds,
      }));

      try {
        // Update session
        await supabase
          .from("game_sessions")
          .update({
            status: "completed",
            completed_at: endedAt,
            game_state: {
              ...gameState,
              score: finalScore,
              obstaclesAvoided,
              timePlayedSeconds,
              isGameOver: true,
              endedAt,
            },
          })
          .eq("id", sessionId);

        // Update user stats
        const { data: existingStats } = await supabase
          .from("user_game_stats")
          .select("*")
          .eq("user_id", activeProfile.id)
          .eq("game_slug", "endless_runner")
          .eq("mode", mode)
          .single();

        const currentStats =
          (existingStats?.stats as RunnerUserStats) || EMPTY_RUNNER_STATS;
        const diffStats = currentStats[gameState.difficulty] || {
          games_played: 0,
          best_score: 0,
          total_score: 0,
          total_obstacles_avoided: 0,
          best_time_seconds: 0,
          total_time_seconds: 0,
        };

        const updatedDiffStats = {
          games_played: diffStats.games_played + 1,
          best_score: Math.max(diffStats.best_score, finalScore),
          total_score: diffStats.total_score + finalScore,
          total_obstacles_avoided:
            diffStats.total_obstacles_avoided + obstaclesAvoided,
          best_time_seconds: Math.max(
            diffStats.best_time_seconds,
            timePlayedSeconds
          ),
          total_time_seconds: diffStats.total_time_seconds + timePlayedSeconds,
        };

        const updatedStats: RunnerUserStats = {
          ...currentStats,
          [gameState.difficulty]: updatedDiffStats,
          total_games: currentStats.total_games + 1,
          total_score: currentStats.total_score + finalScore,
          total_time_seconds:
            currentStats.total_time_seconds + timePlayedSeconds,
        };

        if (existingStats) {
          await supabase
            .from("user_game_stats")
            .update({
              stats: updatedStats,
              games_played: existingStats.games_played + 1,
              personal_best_score: Math.max(
                existingStats.personal_best_score || 0,
                finalScore
              ),
              total_points_earned:
                existingStats.total_points_earned + pointsEarned,
              last_played_at: new Date().toISOString(),
            })
            .eq("id", existingStats.id);
        } else {
          await supabase.from("user_game_stats").insert({
            user_id: activeProfile.id,
            game_slug: "endless_runner",
            mode,
            stats: updatedStats,
            games_played: 1,
            games_won: 1,
            personal_best_score: finalScore,
            total_points_earned: pointsEarned,
            last_played_at: new Date().toISOString(),
          });
        }

        // Weekend mode: add points to team
        if (mode === "weekend" && pointsEarned > 0 && userTeam?.id) {
          // Update team score
          const { data: teamData } = await supabase
            .from("teams")
            .select("score")
            .eq("id", userTeam.id)
            .single();

          if (teamData) {
            await supabase
              .from("teams")
              .update({ score: (teamData.score || 0) + pointsEarned })
              .eq("id", userTeam.id);
          }

          // Send notification
          await notifyTeamOfPoints({
            earnerId: activeProfile.id,
            earnerName: activeProfile.full_name || "Iemand",
            teamId: userTeam.id,
            points: pointsEarned,
            gameName: "Endless Runner",
            reason: `Score: ${finalScore.toLocaleString()}`,
            weekendId: activeWeekend?.id,
            sessionId,
          });

          // Log activity
          await logActivity({
            user_id: activeProfile.id,
            action_type: "score.award",
            action_category: "score",
            target_type: "game_session",
            target_id: sessionId,
            target_name: "Endless Runner",
            metadata: {
              game_slug: "endless_runner",
              points: pointsEarned,
              score: finalScore,
              team_id: userTeam.id,
            },
            weekend_id: activeWeekend?.id,
          });
        }

        // Set result
        setResult({
          score: finalScore,
          distance: finalScore,
          obstaclesAvoided,
          timeSeconds: timePlayedSeconds,
          difficulty: gameState.difficulty,
          pointsEarned,
          isNewHighScore,
        });
      } catch (err) {
        console.error("[useEndlessRunner] Error ending game:", err);
      }
    },
    [
      activeProfile,
      sessionId,
      gameState,
      userTeam,
      activeWeekend,
      mode,
      calculatePoints,
      stopTimer,
    ]
  );

  /**
   * Pause the game
   */
  const pauseGame = useCallback(() => {
    stopTimer();
    setGameState((prev) => ({
      ...prev,
      isPaused: true,
    }));
  }, [stopTimer]);

  /**
   * Resume the game
   */
  const resumeGame = useCallback(() => {
    startTimer();
    setGameState((prev) => ({
      ...prev,
      isPaused: false,
    }));
  }, [startTimer]);

  /**
   * Reset game
   */
  const resetGame = useCallback(() => {
    stopTimer();
    setSessionId(null);
    setResult(null);
    setGameState({
      score: 0,
      highScore: 0,
      speed: 4,
      isPlaying: false,
      isPaused: false,
      isGameOver: false,
      jumps: 0,
      obstaclesAvoided: 0,
      difficulty: "medium",
      timePlayedSeconds: 0,
      startedAt: null,
      endedAt: null,
    });
  }, [stopTimer]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  return {
    gameState,
    sessionId,
    result,
    isLoading,
    startGame,
    updateScore,
    recordObstacleAvoided,
    recordJump,
    endGame,
    pauseGame,
    resumeGame,
    resetGame,
  };
};

